import { useStore } from '@nanostores/react';
import { motion } from 'framer-motion';
import React, { memo, useEffect, useMemo, useState } from 'react';
import type { ScrollToBottom } from 'use-stick-to-bottom';
import { DebugInfoModal } from '~/components/chat/DebugInfoModal';
import { DiscordSupportModal } from '~/components/chat/DiscordSupportModal';
import { MessageItem } from '~/components/chat/MessageItem';
import { LoadingDots } from '~/components/ui/LoadingDots';
import { MessageGradient } from '~/components/ui/MessageGradient';
import { isClaudeCodeAgent } from '~/lib/agents';
import { countErrorRepeats } from '~/lib/error-handling';
import { useChatAgent } from '~/lib/hooks/useChatAgent';
import { chatStore } from '~/lib/stores/chat';
import type { AssistantMessage, ChatMessage, Message } from '~/types/chat';
import type { UserData } from '~/types/user';
import { classNames } from '~/utils/classNames';
import { CC_MAGIC_STRING } from '~/utils/constants';
import { renderLogger } from '~/utils/logger';

interface MessagesProps {
  id?: string;
  className?: string;
  isStreaming?: boolean;
  messages?: Message[];
  messageBoxHeight?: number;
  isAtBottom?: boolean;
  user?: UserData;
  sendMessage?: (messageInput?: string) => void;
  scrollToBottom: ScrollToBottom;
  onRollback?: (message: Message) => void;
  resubmit?: (message: ChatMessage) => void;
  acceptSupabaseChange: (changeId: string) => Promise<void>;
  discardSupabaseChange: (changeId: string, removeMessage?: boolean) => Promise<void>;
}

export const Messages = memo(
  React.forwardRef<HTMLDivElement, MessagesProps>((props: MessagesProps, ref) => {
    renderLogger.trace('Messages');

    const {
      id,
      isStreaming = false,
      isAtBottom = true,
      messages = [],
      className,
      messageBoxHeight = 0,
      onRollback,
      scrollToBottom,
      resubmit,
      acceptSupabaseChange,
      discardSupabaseChange,
      user,
    } = props;

    const [pinnedMessageHeight, setPinnedMessageHeight] = useState(0);
    const [finished, setFinished] = useState(false);
    const isDiscordModalOpen = useStore(chatStore.isDiscordModalOpen);
    const isDebugInfoModalOpen = useStore(chatStore.isDebugInfoModalOpen);
    const [isLoading, setIsLoading] = useState(messages.length === 0);

    const { agent } = useChatAgent();
    const isLoadingPreviousMessages = useStore(chatStore.isLoadingPreviousMessages);

    const mockAssistantMessage: AssistantMessage = {
      id: 'mock-assistant-message',
      createdAt: new Date(),
      role: 'assistant',
      content: isClaudeCodeAgent(agent) ? CC_MAGIC_STRING : '',
      parts: [
        {
          type: 'text',
          text: isClaudeCodeAgent(agent) ? CC_MAGIC_STRING : '',
        },
      ],
      annotations: [],
      toolInvocations: [],
      revisionId: 'initial',
      type: 'chat',
      status: 'streaming',
    };

    const visibleMessages = useMemo(() => {
      const visible = messages.filter(({ hidden }) => !hidden);

      const lastUserMessageIndex = visible.findLastIndex(
        (message) => message.role === 'user' && message.type === 'chat',
      );

      const noStreamingMessages = visible.every((message) => message.status !== 'streaming');

      if (isStreaming && noStreamingMessages) {
        return [
          ...visible.slice(0, lastUserMessageIndex + 1),
          mockAssistantMessage,
          ...visible.slice(lastUserMessageIndex + 1),
        ];
      }

      return visible;
    }, [messages, isStreaming]);

    const hasPinnedMessage = pinnedMessageHeight > 0;

    const lastMessage = visibleMessages.at(-1);

    const isThinking = lastMessage?.role === 'assistant' && lastMessage?.parts?.length;

    const bottom = messageBoxHeight + pinnedMessageHeight;

    const lastChatMessage = visibleMessages.findLast((message) => message.type === 'chat');
    const latestIsProblem = visibleMessages.at(-1)?.type === 'problems';

    const consecutiveProblemCount = latestIsProblem ? countErrorRepeats(visibleMessages)[0] : 0;

    useEffect(() => {
      if (!isStreaming && !isAtBottom) {
        setFinished(true);
      }
    }, [isStreaming]);

    useEffect(() => {
      if (isAtBottom) {
        // timeout to allow gradual fade out
        setTimeout(() => setFinished(false), 150);
      }
    }, [isAtBottom]);

    useEffect(() => {
      if (visibleMessages.length > 0) {
        setIsLoading(false);
      }
    }, [visibleMessages]);

    const lastUserMessageIndex = visibleMessages.findLastIndex(
      (message) => message.role === 'user' && message.type === 'chat' && !message.ignore,
    );

    return (
      <>
        <DiscordSupportModal open={isDiscordModalOpen} onClose={() => chatStore.isDiscordModalOpen.set(false)} />

        {user?.featureFlags?.debugger && (
          <DebugInfoModal
            open={Boolean(isDebugInfoModalOpen)}
            onClose={() => chatStore.isDebugInfoModalOpen.set(undefined)}
          />
        )}

        <section id={id} aria-label="Chat" ref={ref} className={className} translate={isStreaming ? 'no' : undefined}>
          {isLoading && visibleMessages.length === 0 && (
            <div className="w-full text-bolt-elements-textSecondary i-svg-spinners:3-dots-fade size-6" />
          )}

          {isLoadingPreviousMessages && (
            <div className="w-full h-6 mt-4 flex justify-center items-center">
              <LoadingDots text="Loading previous messages" />
            </div>
          )}

          {visibleMessages.map((message, index) => {
            const isLast = index === visibleMessages.length - 1;
            const isLastChatMessage = message.id === lastChatMessage?.id;

            const disableQuickActions =
              isStreaming ||
              (!isLast && (index < visibleMessages.length - 2 || visibleMessages.at(-1)?.type !== 'problems'));

            return (
              <MessageItem
                key={message.id}
                message={message}
                isLast={isLast}
                isLastChatMessage={isLastChatMessage}
                isLastUserMessage={lastUserMessageIndex === index}
                isStreaming={isStreaming}
                disableQuickActions={disableQuickActions}
                setPinnedMessageHeight={setPinnedMessageHeight}
                scrollToBottom={scrollToBottom}
                messageBoxHeight={messageBoxHeight}
                acceptSupabaseChange={acceptSupabaseChange}
                discardSupabaseChange={discardSupabaseChange}
                resubmit={resubmit}
                onRollback={onRollback}
                consecutiveProblemCount={consecutiveProblemCount}
              />
            );
          })}

          {!hasPinnedMessage && !isLoading && !isThinking && visibleMessages.length === 0 && (
            <MessageGradient
              className="sticky flex flex-col justify-center w-full -mb-6"
              style={{ bottom: messageBoxHeight }}
            />
          )}
        </section>

        {scrollToBottom && (
          <motion.div
            className="sticky h-0 mx-auto z-scroller"
            initial={false}
            style={{
              bottom: !hasPinnedMessage ? 38 + bottom + (isStreaming ? 24 : 12) : bottom + (isStreaming ? 24 : 12),
            }}
            transition={{ duration: 0.1 }}
            animate={{
              opacity: isAtBottom ? 0 : 1,
              visibility: isAtBottom ? 'hidden' : 'initial',
              translateY: isAtBottom ? 32 : 0,
            }}
          >
            <button
              className={classNames(
                'flex items-center justify-center size-8 rounded-full shadow shadow-bolt-elements-messages-scroller-shadow',
                {
                  'bg-bolt-elements-messages-scroller-background text-bolt-elements-messages-scroller-icon': !finished,
                  'bg-bolt-elements-messages-scroller-finishedBackground text-bolt-elements-messages-scroller-finishedIcon':
                    finished,
                },
              )}
              onClick={() => scrollToBottom({ animation: { damping: 1, mass: 1.1 } })}
            >
              <div className="i-ph:arrow-down size-4.5" />
            </button>
          </motion.div>
        )}
      </>
    );
  }),
);
